import axios from 'axios'
import Qs from 'qs'
import { getCookie, setCookie } from '../util/cookie'
import tokenManager from '../util/token'
import authSessionPolicy from './modules/authSessionPolicy'

const pendingRequest = new Map()
const { createAuth401Coordinator, isPublicAuthRequest } = authSessionPolicy
const AUTH_REDIRECT_PENDING = new Promise(() => {})
const errorCode = [
  401,
  422,
  1045
]

function normalizeAuthToken(token) {
  if (token === undefined || token === null) {
    return ''
  }
  const value = String(token).trim()
  if (!value || value === 'undefined' || value === 'null') {
    return ''
  }
  return /^Token\s+\S+/.test(value) ? value : `Token ${value}`
}

function isValidAuthToken(token) {
  return /^Token\s+\S+/.test(String(token || '').trim())
}

function normalizeHeaders(headers = {}) {
  if (!headers) {
    return {}
  }
  if (typeof headers === 'string') {
    const authorization = normalizeAuthToken(headers)
    return authorization ? { Authorization: authorization } : {}
  }
  return headers
}

function normalizeRequestArgs(params = {}, headers) {
  if (headers === undefined && typeof params === 'string' && isValidAuthToken(normalizeAuthToken(params))) {
    return {
      params: {},
      headers: normalizeHeaders(params),
    }
  }
  return {
    params,
    headers: normalizeHeaders(headers),
  }
}

function getHeader(headers = {}, key) {
  if (!headers) return undefined
  if (typeof headers.get === 'function') {
    return headers.get(key)
  }
  return headers[key] || headers[key.toLowerCase()]
}

function setHeader(headers = {}, key, value) {
  if (!headers || value === undefined || value === null || value === '') {
    return headers
  }
  if (typeof headers.set === 'function') {
    headers.set(key, value)
  } else {
    headers[key] = value
  }
  return headers
}

function removeHeader(headers = {}, key) {
  if (typeof headers.delete === 'function') {
    headers.delete(key)
  } else {
    delete headers[key]
    delete headers[key.toLowerCase()]
  }
}

function clearAuthStorage() {
  tokenManager.clearToken()
  ;['LoginRouter', 'DashboardRouter', 'LoginInfo', 'userInfo', 'project']
    .forEach(key => localStorage.removeItem(key))
}

function syncTokenFromResponse(response) {
  const headers = response && response.headers ? response.headers : {}
  const requestUrl = response && response.config ? response.config.url : ''
  if (isPublicAuthRequest(requestUrl)) {
    auth401Coordinator.reset()
  }
  const renewedToken = getHeader(headers, 'X-Renewed-Token') || getHeader(headers, 'X-Access-Token') || getHeader(headers, 'Authorization')
  const rememberHeader = String(getHeader(headers, 'X-Remember-Me') || '').toLowerCase()
  const token = normalizeAuthToken(renewedToken)
  if (isValidAuthToken(token)) {
    auth401Coordinator.reset()
    tokenManager.setToken(
      token,
      rememberHeader === 'true' || (rememberHeader !== 'false' && tokenManager.isRemembered())
    )
  }
}

const auth401Coordinator = createAuth401Coordinator({
  clearSession: clearAuthStorage,
  getToken: () => normalizeAuthToken(tokenManager.getToken()),
  redirectToLogin: () => tokenManager.redirectToLogin(),
  refreshToken: () => tokenManager.refreshToken(),
  retryRequest: (config, token) => {
    config.headers = normalizeHeaders(config.headers)
    setHeader(config.headers, 'Authorization', token)
    return axios(config)
  },
  stopRequest: () => AUTH_REDIRECT_PENDING
})

function generateReqKey(config) {
  const { method, url, params, data } = config
  return [method, url, Qs.stringify(params), Qs.stringify(data)].join('&')
}

function addPendingRequest(config) {
  const requestKey = generateReqKey(config)
  config.cancelToken =
    config.cancelToken ||
    new axios.CancelToken(cancel => {
      if (!pendingRequest.has(requestKey)) {
        pendingRequest.set(requestKey, cancel)
      }
    })
}

function removePendingRequest(config) {
  const requestKey = generateReqKey(config)
  if (pendingRequest.has(requestKey)) {
    const cancel = pendingRequest.get(requestKey)
    // 只删除记录，不取消请求
    pendingRequest.delete(requestKey)
  }
}

function cancelPendingRequest(config) {
  const requestKey = generateReqKey(config)
  if (pendingRequest.has(requestKey)) {
    const cancel = pendingRequest.get(requestKey)
    // 取消请求并删除记录
    cancel(requestKey)
    pendingRequest.delete(requestKey)
  }
}

axios.interceptors.request.use(
  async function (config) {
    // 取消之前相同的待处理请求（防止重复请求）
    cancelPendingRequest(config)
    // 添加当前请求到待处理列表
    addPendingRequest(config)

    // Skip session check for public endpoints
    config.headers = normalizeHeaders(config.headers)
    const isPublicEndpoint = isPublicAuthRequest(config.url)
    const requestAuthorization = normalizeAuthToken(getHeader(config.headers, 'Authorization'))
    const hasAuthorizationHeader = isValidAuthToken(requestAuthorization)
    if (hasAuthorizationHeader) {
      setHeader(config.headers, 'Authorization', requestAuthorization)
    } else {
      removeHeader(config.headers, 'Authorization')
    }
    if (!isPublicEndpoint) {
      // Check session before making request
      if (!hasAuthorizationHeader) {
        const isSessionValid = await tokenManager.checkSession()
        if (!isSessionValid) {
          return auth401Coordinator.expireSession()
        }

        const token = normalizeAuthToken(tokenManager.getToken())
        if (!isValidAuthToken(token)) {
          return auth401Coordinator.expireSession()
        }
        setHeader(config.headers, 'Authorization', token)
      } else {
        setHeader(config.headers, 'Authorization', requestAuthorization)
      }
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

axios.interceptors.response.use(
  response => {
    // 请求成功，清理待处理列表
    if (response) {
      removePendingRequest(response.config)
      syncTokenFromResponse(response)
    }
    return response
  },
  error => {
    // 请求失败，清理待处理列表
    removePendingRequest(error.config || {})

    // 如果是取消的请求，静默处理，不抛出错误
    if (axios.isCancel(error)) {
      // console.log('请求被取消（防重复）：' + error.message)
      // 返回一个永远不会 resolve 的 Promise，让调用方的 .then() 和 .catch() 都不会执行
      return AUTH_REDIRECT_PENDING
    }
    return auth401Coordinator.handle(error)
  }
)

const commonParams = {}

const CancelToken = axios.CancelToken
let _cancel = {}

export function setCommonParams(obj) {
  Object.assign(commonParams, obj)
}

function resolveUrl(url, params = {}) {
  if (!params || typeof params !== 'object') {
    return url
  }
  return url.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (match, key) => {
    if (params[key] === undefined || params[key] === null || params[key] === '') {
      return match
    }
    const value = encodeURIComponent(params[key])
    delete params[key]
    return value
  })
}

export function get(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    let _head = { 'Content-Type': 'application/x-www-form-urlencoded' }
    if (headers) {
      setHeader(headers, 'Content-Type', 'application/x-www-form-urlencoded')
      _head = headers
    }
    return handleAjax(
      axios.get(url, {
        headers: _head,
        params: Object.assign({}, commonParams, params),
        cancelToken: new CancelToken(function executor(c) {
          _cancel[url] = c
        })
      })
    )
  }
}

export function getJson(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const resolvedUrl = resolveUrl(url, params)
    let _head = { 'Content-Type': 'application/json' }
    if (headers) {
      setHeader(headers, 'Content-Type', 'application/json')
      _head = headers
    }
    return handleAjax(
      axios.get(resolvedUrl, {
        headers: _head,
        params: Object.assign({}, commonParams, params)
      })
    )
  }
}


export function post1(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    let _head = { 'Content-Type': 'application/x-www-form-urlencoded' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/x-www-form-urlencoded')
      _head = headers
    }
    return handleAjax(
      axios.post(url, params, {
        headers: _head,
        cancelToken: new CancelToken(function executor(c) {
          _cancel[url] = c
        })
      })
    )
  }
}

export function post(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    let _head = { 'Content-Type': 'application/x-www-form-urlencoded' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/x-www-form-urlencoded')
      _head = headers
    }
    return handleAjax(
      axios.post(url, param(Object.assign({}, commonParams, params)), {
        headers: _head,
        cancelToken: new CancelToken(function executor(c) {
          _cancel[url] = c
        })
      })
    )
  }
}

export function postJson(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const resolvedUrl = resolveUrl(url, params)

    let _head = { 'Content-Type': 'application/json' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/json')
      _head = headers
    }
    return handleAjax(
      axios.post(resolvedUrl, params, {
        headers: _head
      })
    )
  }
}

export function postJsonRaw(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const resolvedUrl = resolveUrl(url, params)

    let _head = { 'Content-Type': 'application/json' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/json')
      _head = headers
    }
    return axios.post(resolvedUrl, params, {
      headers: _head
    }).then(res => {
      if (res && res.status === 204) {
        return null
      }
      if (!res) {
        throw new Error('请求失败，未收到服务器响应')
      }
      return res.data
    })
  }
}

export function postForm(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const formData = params.formData instanceof FormData ? params.formData : params
    delete params.formData
    const resolvedUrl = resolveUrl(url, params)
    // FormData 会自动设置 Content-Type 为 multipart/form-data
    // 不要手动设置，让浏览器自动处理 boundary
    return handleAjax(
      axios.post(resolvedUrl, formData, {
        headers: headers || {},
        params: Object.assign({}, commonParams, params)
      })
    )
  }
}

export function postExcel(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    let _head = {
      'Content-Type': 'application/json',
      'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/json')
      setHeader(headers, 'Accept', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
      _head = headers
    }
    return axios.post(url, params, {
      headers: _head,
      responseType: 'arraybuffer'
    }).then(response => {
      if (response.headers['content-type'].includes('application/json')) {
        const decoder = new TextDecoder('utf-8');
        const text = decoder.decode(response.data);
        try {
          const error = JSON.parse(text);
          return Promise.reject(new Error(error.message || '导出失败'));
        } catch {
          return Promise.reject(new Error('导出失败'));
        }
      }
      return response.data;
    });
  }
}

export function put(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const resolvedUrl = resolveUrl(url, params)
    let _head = { 'Content-Type': 'application/json;charset=utf-8' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/json;charset=utf-8')
      _head = headers
    }
    return handleAjax(
      axios.put(resolvedUrl, params, {
        headers: _head
      })
    )
  }
}

export function patch(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const resolvedUrl = resolveUrl(url, params)
    let _head = { 'Content-Type': 'application/json;charset=utf-8' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/json;charset=utf-8')
      _head = headers
    }
    return handleAjax(
      axios.patch(resolvedUrl, params, {
        headers: _head
      })
    )
  }
}

export function del(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    let _head = { 'Content-Type': 'application/x-www-form-urlencoded' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/x-www-form-urlencoded')
      _head = headers
    }
    return handleAjax(
      axios.delete(url, {
        headers: _head,
        params: Object.assign({}, commonParams, params),
        cancelToken: new CancelToken(function executor(c) {
          _cancel[url] = c
        })
      })
    )
  }
}

export function delJson(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const resolvedUrl = resolveUrl(url, params)
    let _head = { 'Content-Type': 'application/json' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/json')
      _head = headers
    }
    return handleAjax(
      axios.delete(resolvedUrl, {
        headers: _head,
        params: Object.assign({}, commonParams, params)
      })
    )
  }
}

export function delBodyJson(url) {
  return function (params = {}, headers) {
    ({ params, headers } = normalizeRequestArgs(params, headers))
    params = Object.assign({}, params)
    const resolvedUrl = resolveUrl(url, params)
    let _head = { 'Content-Type': 'application/json' }
    if (headers !== undefined) {
      setHeader(headers, 'Content-Type', 'application/json')
      _head = headers
    }
    return handleAjax(
      axios.delete(resolvedUrl, {
        headers: _head,
        data: Object.assign({}, commonParams, params),
        cancelToken: new CancelToken(function executor(c) {
          _cancel[resolvedUrl] = c
        })
      })
    )
  }
}

export function cancel(url) {
  return function () {
    _cancel[url] && _cancel[url]()
  }
}

function userOffLine(token) {
  let param = { token: token }
  axios.post('/api/user_offline', param)
}

function handleAjax(ajaxPromise) {
  return ajaxPromise
    .then(res => {

      if (res && res.status === 204) {
        return null
      }

      // 检查响应是否存在
      if (!res || !res.data) {
        console.error('handleAjax - 响应为空或缺少 data 字段:', res)
        // 抛出错误，让调用方的 .catch() 捕获
        throw new Error('请求失败，未收到服务器响应')
      }

      let serverData = res.data
      if (serverData.code === undefined) {
        return serverData
      }

      // 处理成功响应
      if (serverData.code === 200) {
        return serverData.data
      }

      // 处理 1045 账户被禁用
      if (serverData.code === 1045) {
        localStorage.clear()
        window.location.href = '#/account-disabled'
        throw new Error(serverData.message || '账户已被禁用')
      }

      // 处理 401 未授权
      if (serverData.code === 401) {
        return auth401Coordinator.expireSession()
      }

      // 处理 500 服务器错误 - 抛出错误让调用方处理
      if (serverData.code === 500) {
        console.error('handleAjax - 服务器内部错误:', serverData)
        throw new Error(serverData.message || '内部服务器错误')
      }

      // 处理 422 和其他错误 - 抛出错误让调用方处理
      if (serverData.code === 422 || serverData.code >= 400) {
        throw new Error(serverData.message || '请求失败')
      }

      // 其他未知错误
      throw new Error(serverData.message || '未知错误')
    })
    .catch(e => {
      console.log(e)

      // 处理 axios 错误对象
      if (e.response) {
        const { status, data } = e.response
        if (status === 500) {
          const errorMsg = data?.message || '内部服务器错误'
          throw new Error(errorMsg)
        } else if (status === 400) {
          const errorMsg = data?.message || '请求参数错误'
          throw new Error(errorMsg)
        } else if (status === 401) {
          throw new Error(data?.message || '未授权')
        } else if (data && data.message) {
          throw new Error(data.message)
        } else {
          throw new Error(`请求失败，状态码: ${status}`)
        }
      }

      // 如果已经是 Error 对象，直接抛出让调用方处理
      if (e instanceof Error) {
        throw e
      }

      // 如果是取消请求
      if (axios.isCancel(e)) {
        throw new Error(e.message || '请求已取消')
      }

      // 网络错误或其他错误
      if (e.message) {
        throw new Error(e.message)
      }

      // 其他未知错误
      throw new Error('请求失败')
    })
}

function handleError(serverData) {
  return Promise.reject(new Error(serverData.message))
}

function param(obj) {
  if (!obj) {
    return ''
  }
  let s = []
  for (let prefix in obj) {
    buildParams(prefix, obj[prefix], add)
  }

  function add(key, value) {
    s[s.length] = encodeURIComponent(key) + '=' + encodeURIComponent(value)
  }

  return s.join('&')
}

const rbracket = /\[\]$/

function buildParams(prefix, obj, add) {
  let name
  if (Array.isArray(obj)) {
    // Serialize array item.
    obj.forEach(function (v, i) {
      if (rbracket.test(prefix)) {
        // Treat each array item as a scalar.
        add(prefix, v)
      } else {
        // Item is non-scalar (array or object), encode its numeric index.
        buildParams(
          prefix + '[' + (typeof v === 'object' && v !== null ? i : '') + ']',
          v,
          add
        )
      }
    })
  } else if (typeof obj === 'object') {
    // Serialize object item.
    for (name in obj) {
      buildParams(prefix + '[' + name + ']', obj[name], add)
    }
  } else {
    // Serialize scalar item.
    add(prefix, obj)
  }
}
